import React, { useState, useEffect } from "react";
import BluetoothSensor from "../BluetoothSensor";
import SpeakerController from "../SpeakerController";
import { useToast } from "../../hooks/useToast";
import ToastContainer from "../ToastContainer";
import {
  playOnMultipleDevices,
  stopAll,
  pauseAll,
  resumeAll,
  setTrack,
} from "../../services/music.service";
import "../../pages/home.css";

// ─── Types ─────────────────────────────────────────────
type HzData = number | { ch1: number; ch2: number; ch3: number };

interface DashboardModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ✅ Only one track
const TRACK = {
  label: "Interstellar",
  file: "/music/interstellar.mp3",
  emoji: "🌌",
};

const DashboardModal: React.FC<DashboardModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [mounted, setMounted] = useState(false);
  const [hz, setHz] = useState(0);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [isSensorConnected, setIsSensorConnected] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const { toasts, toast, dismiss } = useToast();

  useEffect(() => setTrack(TRACK.file), []);
useEffect(() => {
  if (selectedDeviceIds.length === 0) {
    // Stop everything if no devices selected
    stopAll();
    setIsPlaying(false);
    setIsPaused(false);

    toast({
      type: "info",
      title: "Playback Stopped",
      message: "All devices removed. Music stopped.",
    });
  }
}, [selectedDeviceIds]);
  useEffect(() => {
    if (isOpen) setMounted(true);
    else setTimeout(() => setMounted(false), 250);
  }, [isOpen]);
useEffect(() => {
  if (isSensorConnected && selectedDeviceIds.length > 0) {
    // Close modal
    onClose();

    // 🔥 Notify app to go to practice screen
    window.dispatchEvent(new Event("yogyism:startPractice"));
    window.dispatchEvent(new Event("yogyism:devicesReady"));
  }
}, [isSensorConnected, selectedDeviceIds]);
  if (!mounted) return null;

  const handleHzChange = (data: HzData) => {
    if (typeof data === "object") setHz(data.ch1);
    else setHz(data);
  };

  const handlePlay = async () => {
    if (!selectedDeviceIds.length) {
      toast({
        type: "warning",
        title: "No Speaker",
        message: "Select device",
      });
      return;
    }
    stopAll();
    await playOnMultipleDevices(selectedDeviceIds);
    setIsPlaying(true);
    setIsPaused(false);
  };

  const handlePause = () => {
    pauseAll();
    setIsPlaying(false);
    setIsPaused(true);
  };

  const handleResume = () => {
    resumeAll();
    setIsPlaying(true);
    setIsPaused(false);
  };

  const handleStop = () => {
    stopAll();
    setIsPlaying(false);
    setIsPaused(false);
  };

  const handlePlayPause = () => {
    if (isPlaying) handlePause();
    else if (isPaused) handleResume();
    else handlePlay();
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div
          className="card"
          style={{ width: 700, maxHeight: "90vh", overflowY: "auto" ,overflowX: "hidden" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close */}
          <button className="btn disconnect" onClick={onClose}>
            ✕ Close
          </button>

          {/* Bluetooth Sensor */}
          <BluetoothSensor
            onHzChange={handleHzChange}
             onConnectionChange={(connected: boolean) => {
    setIsSensorConnected(connected);
  }}
            toast={toast}
          />

          {/* Hz Visual */}
          <div className="hz-box">
            <h3>Eye Movement Frequency</h3>
            <div className="hz-value">{hz}</div>
            <div className="hz-bar">
              <div
                className="hz-fill"
                style={{ width: `${Math.min(hz, 100)}%` }}
              />
            </div>
          </div>

          {/* Music Section */}
     

          {/* Speakers */}
          <SpeakerController
            selected={selectedDeviceIds}
            onChange={setSelectedDeviceIds}
            toast={toast}
          />
               <div className="speaker-box">
            <h3>Music</h3>

            <button className="btn sound">
              {TRACK.emoji} {TRACK.label}
            </button>

            <div className="music-controls-inline">
              <button
                className={`music-btn music-btn-play ${
                  isPlaying ? "active" : ""
                }`}
                onClick={handlePlayPause}
              >
                {isPlaying
                  ? "⏸ Pause"
                  : isPaused
                  ? "▶ Resume"
                  : "▶ Play"}
              </button>

              <button
                className="music-btn music-btn-stop"
                onClick={handleStop}
                disabled={!isPlaying && !isPaused}
              >
                ■ Stop
              </button>
            </div>

            {isPaused && <div className="paused-hint">Paused</div>}
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} dismiss={dismiss} />
    </>
  );
};

export default DashboardModal;